import React, { useCallback, useMemo, useState } from 'react';

import Table from '@components/common/Table';

import { useConnectionLogsFilters } from '@hooks/middlewareSMO/useConnectionLogsFilters';
import useQueryConnectionLogs from '@hooks/middlewareSMO/useQueryConnectionLogs';

import { TSelectOption } from '@interfaces/components/componentTypes';
import {
	ISortBy,
	ITablePagination,
	ITableSort,
	TTableFilter,
} from '@interfaces/components/table';

import { defaultMeta } from '@enums/common';

import { getColumns } from './connectionLogsColumns';
import { generateConnectionLogsFilters, getConnectionLogsFilterValues } from './utils';

import styles from '../ManageMiddlewareSMO.module.scss';

const ConnectionLogs = () => {
	const [page, setPage] = useState(defaultMeta.page);
	const [pageSize, setPageSize] = useState(defaultMeta.pageSize);
	const [sortDirection, setSortDirection] = useState(false);
	const [sortFieldName, setSortFieldName] = useState<string | null>(null);
	const [selectedFilters, setSelectedFilters] = useState<Record<string, TSelectOption>>({});

	const { filters: filtersList, isLoading: isLoadingFilters } = useConnectionLogsFilters();
	const filterValues = useMemo(() => {
		return getConnectionLogsFilterValues(selectedFilters);
	}, [selectedFilters]);

	const { connectionLogsData, countOfRecords, isLoadingConnectionLogs } = useQueryConnectionLogs({
		page,
		pageSize,
		sortDirection,
		sortFieldName,
		...filterValues,
	});

	const columns = useMemo(() => getColumns(), []);

	const clearPagination = useCallback(() => {
		setPage(defaultMeta.page);
	}, []);

	const setFilters = useCallback((value: Record<string, TSelectOption>) => {
		clearPagination();
		setSelectedFilters((prevState) => ({
			...prevState,
			...value,
		}));
	}, [clearPagination]);

	const pageHandler = useCallback((page: number) => setPage(page), []);
	const pageSizeHandler = useCallback((pageSize: number) => {
		setPageSize(pageSize);
		setPage(defaultMeta.page);
	}, []);

	const handleSort = useCallback((sortBy: Array<ISortBy>) => {
		setSortDirection(!!sortBy[0]?.desc);
		setSortFieldName(sortBy[0]?.id);
	}, []);

	const sort: ITableSort = useMemo(() => ({
		initialState: [{ id: sortFieldName, desc: sortDirection }],
		handleSort,
	}), [sortFieldName, sortDirection, handleSort]);

	const filters: TTableFilter[] = useMemo(() => {
		return generateConnectionLogsFilters(
			filtersList,
			selectedFilters,
			setFilters,
		);
	}, [filtersList, selectedFilters, setFilters]);

	const pagination: ITablePagination = useMemo(() => ({
		countOfRecords,
		forcePage: page,
		size: pageSize,
		pageHandler,
		pageSizeHandler,
	}), [page, pageSize, countOfRecords, pageHandler, pageSizeHandler]);

	const isOpenFilters = useMemo(() => {
		const hasActiveFilters = !!Object.values(selectedFilters).filter(Boolean).length;
		return hasActiveFilters ? true : null;
	}, [selectedFilters]);

	const isLoadingTable = isLoadingConnectionLogs || isLoadingFilters;

	return (
		<div className={styles.tableContainer}>
			<Table
				columns={columns}
				data={connectionLogsData}
				filters={filters}
				isLoading={isLoadingTable}
				isOpenFilters={isOpenFilters}
				isWithoutCheckbox
				isWithoutDeleteAction
				pagination={pagination}
				sort={sort}
			/>
		</div>
	);
};

export default ConnectionLogs;
